import { FC, ReactNode } from "react";
import { IconButton, InputAdornment, MenuItem, TextField, Tooltip } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { Search as SearchIcon } from "lucide-react";
import "../Invoice/Invoice.css"; // provides the "c-text-field-3" rounded-pill style

// Shared filter-bar primitives for the inventory listings. They reproduce the
// look of the Items filter row: a circular reset button on the left followed by
// borderless "c-text-field-3" rounded-pill fields, with the page's primary
// action (e.g. New X) pushed to the right.

const FONT = { fontSize: 14 } as const;

export const FilterBar: FC<{ onReset?: () => void; action?: ReactNode; children: ReactNode }> = ({ onReset, action, children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", flexWrap: "wrap" }}>
    {onReset && (
      <Tooltip title="Reset Filter">
        <IconButton
          onClick={onReset}
          size="small"
          style={{ color: "#488dff", backgroundColor: "#fff", border: "3px solid #e9ecf1" }}
        >
          <Refresh />
        </IconButton>
      </Tooltip>
    )}
    {children}
    {action && <div style={{ marginLeft: "auto" }}>{action}</div>}
  </div>
);

export const FilterSearch: FC<{ value: string; onChange: (v: string) => void; placeholder?: string; width?: number | string }> = ({
  value,
  onChange,
  placeholder = "Search...",
  width = 280,
}) => (
  <TextField
    className="c-text-field-3"
    variant="standard"
    size="small"
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    style={{ width }}
    InputProps={{
      disableUnderline: true,
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon style={{ marginTop: -3 }} size={18} />
        </InputAdornment>
      ),
      style: FONT,
    }}
  />
);

export const FilterSelect: FC<{
  value: string;
  onChange: (v: string) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  width?: number | string;
}> = ({ value, onChange, options, placeholder = "Select", width = 180 }) => (
  <TextField
    className="c-text-field-3"
    variant="standard"
    size="small"
    select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{ width }}
    SelectProps={{ displayEmpty: true }}
    InputProps={{ disableUnderline: true, style: { ...FONT, color: value ? "inherit" : "#666" } }}
  >
    <MenuItem value="">{placeholder}</MenuItem>
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </TextField>
);

export const FilterDate: FC<{ value: string; onChange: (v: string) => void; label?: string; width?: number | string; min?: string }> = ({
  value,
  onChange,
  label,
  width = 170,
  min,
}) => (
  <TextField
    size="small"
    type="date"
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{ width }}
    InputLabelProps={{ shrink: true }}
    inputProps={{ min }}
    InputProps={{ style: FONT }}
    sx={{
      backgroundColor: "#fff",
      "& .MuiOutlinedInput-root": { borderRadius: "10px", height: 40 },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e9ecf1", borderWidth: "2px" },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d7dce4" },
    }}
  />
);
